/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Typography, Typography2 } from '@coursera/cds-core';

import { ModalStates } from 'bundles/enterprise-admin-program-creation/constants';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/enterprise-admin-program-creation';

const modalContainer = () =>
  css({
    padding: 'var(--cds-spacing-200)',
  });

const getModalSectionStyles = (hasDescription?: boolean) =>
  css({
    marginBottom: hasDescription ? 'var(--cds-spacing-400)' : 'var(--cds-spacing-800)',
  });

export type Props = {
  customError?: string;
  modalState?: ModalStates;
  onClose: () => void;
  onDeleteDomainConfirmClick: () => Promise<void>;
};

export const InviteLearnersByAllowlistModal = ({
  customError,
  modalState,
  onClose,
  onDeleteDomainConfirmClick,
}: Props) => {
  if (!modalState) {
    return null;
  }

  const getModalContent = () => {
    let modalTitle = '';
    let modalDescription = '';
    if (modalState === ModalStates.AddError) {
      modalTitle = _t('Error adding domain(s)');
      modalDescription = _t(
        'There was an error. Please try again later. If the problem persists, please contact Coursera support.'
      );
    } else if (modalState === ModalStates.DeleteConfirm) {
      modalTitle = _t('Remove this domain permanently');
      modalDescription = _t(
        'If any learners that have already joined using this domain, they will not be affected by this change. By removing it now, only new learners that try to join using this domain name will no longer be able to access it.'
      );
    } else if (modalState === ModalStates.DeleteSuccess) {
      modalTitle = _t('Domain successfully removed!');
    } else if (modalState === ModalStates.DuplicateDomainNames) {
      modalTitle = _t('Duplicate domain names are not allowed!');
    } else if (modalState === ModalStates.DeleteError) {
      modalTitle = _t('Error removing domain');
      modalDescription = _t(
        'There was an error removing this domain. Please try again later. If the problem persists, please contact Coursera support.'
      );
    }

    return { modalTitle, modalDescription };
  };

  const getModalButtons = () => {
    if (modalState === ModalStates.DeleteConfirm) {
      return (
        <React.Fragment>
          <Button variant="primary" css={css({ marginRight: '24px' })} onClick={onDeleteDomainConfirmClick}>
            {_t('Remove')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {_t('Cancel')}
          </Button>
        </React.Fragment>
      );
    }

    return (
      <Button variant="primary" onClick={onClose}>
        {_t('Got it')}
      </Button>
    );
  };

  const { modalTitle, modalDescription } = getModalContent();

  return (
    <Modal modalName="allowlist-modal" handleClose={onClose}>
      <div css={modalContainer()}>
        <Typography css={getModalSectionStyles(!!modalDescription)} variant="h1">
          {modalTitle}
        </Typography>
        {customError && (
          <Typography2 component="p" css={getModalSectionStyles(true)} variant="bodyPrimary">
            {customError}
          </Typography2>
        )}
        {modalDescription && !customError ? (
          <Typography2 component="p" css={getModalSectionStyles(true)} variant="bodyPrimary">
            {modalDescription}
          </Typography2>
        ) : null}
        <div>{getModalButtons()}</div>
      </div>
    </Modal>
  );
};
